import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
    clearSession,
    findUserByIdentifier,
    getSessionUser,
    getUsers,
    setSessionUser,
    upsertUser,
} from "@/lib/storage";
import { hashPassword, verifyPassword } from "@/lib/hash";
import { getReferralCode, recordReferral } from "@/lib/referralStore";
import { mirror, hydrateFromSupabase } from "@/lib/syncStore";
import { SUPABASE_TABLES, supabase } from "@/lib/supabaseClient";
import { silentRefreshGeo } from "@/lib/userGeoStore";

const fireMirror = (table, row) => { try { mirror(table, row); } catch {} };

const looksLikeEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(s || "").trim());

/** Strip secrets before mirroring user/profile rows to Supabase. */
const sanitizeUserForMirror = (u) => {
    if (!u) return null;
    const { passwordHash: _ph, ...safe } = u;
    return safe;
};

const AuthContext = createContext(null);

const genId = () =>
    "u_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

const normalize = (s) => String(s || "").trim();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => getSessionUser());

    // App boot: silent geolocation refresh (no prompt) + Supabase read
    // hydration so fresh devices pull data from the DB.
    useEffect(() => {
        silentRefreshGeo();
        hydrateFromSupabase().catch(() => {});
    }, []);

    // Keep state in sync if localStorage is modified in another tab
    useEffect(() => {
        const handler = () => setUser(getSessionUser());
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, []);

    // Auto-session: when Supabase reports a SIGNED_IN event (e.g. the user
    // just clicked the email confirmation link), find or hydrate the matching
    // local user record so the regular app routes start working immediately.
    // Unconfirmed users are explicitly refused.
    useEffect(() => {
        if (!supabase) return undefined;
        const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
            if (event !== "SIGNED_IN" && event !== "INITIAL_SESSION") return;
            const sUser = session?.user;
            if (!sUser?.email) return;
            if (!sUser.email_confirmed_at && !sUser.confirmed_at) {
                // Should not normally happen but guard anyway.
                supabase.auth.signOut().catch(() => {});
                return;
            }
            const email = String(sUser.email).toLowerCase();
            let local = findUserByIdentifier(email);
            if (!local) {
                const meta = sUser.user_metadata || {};
                const now = new Date().toISOString();
                local = {
                    id: sUser.id,
                    accountType: meta.accountType || "personal",
                    fullName: meta.fullName || meta.full_name || email.split("@")[0],
                    username: meta.username || email.split("@")[0],
                    email,
                    phone: null,
                    avatar: meta.avatar || null,
                    passwordHash: "",
                    createdAt: now,
                    nameChangedAt: now,
                    usernameChangedAt: now,
                    acceptedTerms: true,
                };
                upsertUser(local);
            }
            setSessionUser(local.id);
            setUser(local);
        });
        return () => sub?.subscription?.unsubscribe?.();
    }, []);

    const signIn = useCallback(async (identifier, password) => {
        const id = normalize(identifier);
        if (!id || !password) {
            return { ok: false, error: "Please fill in all fields" };
        }

        // For email identifiers, Supabase Auth is the **only** source of
        // truth — we never fall through to the local hash store. This keeps
        // unconfirmed users out of the app.
        if (looksLikeEmail(id)) {
            if (!supabase) {
                return {
                    ok: false,
                    error: "Authentication is unavailable. Please try again later.",
                };
            }
            try {
                const { data, error } = await supabase.auth.signInWithPassword({
                    email: id,
                    password,
                });
                if (error) {
                    const msg = (error.message || "").toLowerCase();
                    if (msg.includes("not confirmed") || msg.includes("confirm")) {
                        return {
                            ok: false,
                            error: "Please confirm your email before signing in.",
                        };
                    }
                    if (msg.includes("invalid login")) {
                        return { ok: false, error: "Incorrect login details" };
                    }
                    return { ok: false, error: error.message || "Sign-in failed." };
                }
                if (!data?.user) {
                    return { ok: false, error: "Sign-in failed. Please try again." };
                }
                // Belt-and-braces: refuse the session if Supabase didn't
                // mark the email as confirmed.
                if (!data.user.email_confirmed_at && !data.user.confirmed_at) {
                    await supabase.auth.signOut();
                    return {
                        ok: false,
                        error: "Please confirm your email before signing in.",
                    };
                }

                // Hydrate / find the local user record.
                let local = findUserByIdentifier(id);
                if (!local) {
                    const meta = data.user.user_metadata || {};
                    const now = new Date().toISOString();
                    local = {
                        id: data.user.id,
                        accountType: meta.accountType || "personal",
                        fullName: meta.fullName || meta.full_name || id.split("@")[0],
                        username: meta.username || id.split("@")[0],
                        email: id,
                        phone: null,
                        avatar: meta.avatar || null,
                        passwordHash: hashPassword(password),
                        createdAt: now,
                        nameChangedAt: now,
                        usernameChangedAt: now,
                        acceptedTerms: true,
                    };
                    upsertUser(local);
                }
                setSessionUser(local.id);
                setUser(local);
                return { ok: true };
            } catch {
                return {
                    ok: false,
                    error: "Could not reach the auth service. Please try again.",
                };
            }
        }

        // Phone / username identifiers — local hash auth only.
        const existing = findUserByIdentifier(id);
        if (!existing) {
            return { ok: false, error: "Account not found" };
        }
        if (!verifyPassword(password, existing.passwordHash)) {
            return { ok: false, error: "Incorrect login details" };
        }
        setSessionUser(existing.id);
        setUser(existing);
        return { ok: true };
    }, []);

    const identifierIsTaken = useCallback((identifier) => {
        return !!findUserByIdentifier(identifier);
    }, []);

    const usernameIsTaken = useCallback((username) => {
        const u = normalize(username).toLowerCase();
        if (!u) return false;
        return getUsers().some((x) => x.username?.toLowerCase() === u);
    }, []);

    const signUp = useCallback((draft) => {
        // draft = { method, phone?, email?, fullName, username, dob, password, avatar? }
        if (!draft?.fullName || !draft?.username || !draft?.password) {
            return { ok: false, error: "Missing required details" };
        }
        if (usernameIsTaken(draft.username)) {
            return { ok: false, error: "Username already taken" };
        }
        const now = new Date().toISOString();
        const newUser = {
            id: genId(),
            accountType: draft.accountType === "business" ? "business" : "personal",
            fullName: normalize(draft.fullName),
            username: normalize(draft.username),
            email: normalize(draft.email) || null,
            phone: normalize(draft.phone) || null,
            dob: draft.dob || null,
            avatar: draft.avatar || null,
            passwordHash: hashPassword(draft.password),
            createdAt: now,
            nameChangedAt: now,
            usernameChangedAt: now,
            acceptedTerms: !!draft.acceptedTerms,
        };
        upsertUser(newUser);
        setSessionUser(newUser.id);
        setUser(newUser);
        // Mirror the new account to Supabase tables (no-op if not configured).
        fireMirror(SUPABASE_TABLES.USERS, sanitizeUserForMirror(newUser));
        fireMirror(SUPABASE_TABLES.PROFILES, {
            id: newUser.id,
            userId: newUser.id,
            fullName: newUser.fullName,
            username: newUser.username,
            email: newUser.email,
            phone: newUser.phone,
            avatar: newUser.avatar,
            accountType: newUser.accountType,
            createdAt: newUser.createdAt,
        });

        // Capture referral attribution — link the new user to whoever invited them.
        try {
            const refCode = (draft.referralCode || "").trim().toUpperCase() ||
                (() => {
                    try {
                        const url = new URL(window.location.href);
                        return (url.searchParams.get("ref") || "").trim().toUpperCase();
                    } catch { return ""; }
                })();
            if (refCode) {
                const all = getUsers();
                const referrer = all.find((u) => u.id !== newUser.id && getReferralCode(u.id) === refCode);
                if (referrer) recordReferral(referrer.id, newUser.id);
            }
        } catch {}

        return { ok: true, user: newUser };
    }, [usernameIsTaken]);

    const signOut = useCallback(() => {
        clearSession();
        setUser(null);
    }, []);

    const resetPassword = useCallback((identifier, newPassword) => {
        const existing = findUserByIdentifier(identifier);
        if (!existing) return { ok: false, error: "Account not found" };
        const updated = { ...existing, passwordHash: hashPassword(newPassword) };
        upsertUser(updated);
        if (user?.id === updated.id) setUser(updated);
        fireMirror(SUPABASE_TABLES.USERS, sanitizeUserForMirror(updated));
        return { ok: true };
    }, [user]);

    const updateUser = useCallback((patch) => {
        if (!user) return { ok: false, error: "Not signed in" };
        const updated = { ...user, ...patch };
        upsertUser(updated);
        setUser(updated);
        fireMirror(SUPABASE_TABLES.USERS, sanitizeUserForMirror(updated));
        fireMirror(SUPABASE_TABLES.PROFILES, {
            id: updated.id,
            userId: updated.id,
            fullName: updated.fullName,
            username: updated.username,
            email: updated.email,
            phone: updated.phone,
            avatar: updated.avatar,
            accountType: updated.accountType,
            updatedAt: new Date().toISOString(),
        });
        return { ok: true, user: updated };
    }, [user]);

    const value = {
        user,
        isAuthenticated: !!user,
        signIn,
        signUp,
        signOut,
        resetPassword,
        updateUser,
        identifierIsTaken,
        usernameIsTaken,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
